/* ─────────────────────────────────────────────────────────────────────
   Tykables 7.0 — Size auto-select (tyk-size-autoselect.js)

   On PDP load:
     1. Detect the product's size-chart type (from a data attribute on the
        PDP form or product tags surfaced via the section).
     2. Read the saved size from localStorage (tyk.size.{type}).
     3. Find a matching Size option in the variant picker and click it.

   Also exposes:
     window.tykSizeAutoSelect.applyToNearestForm(trigger, sizeId)
       — Called by the quiz on completion so "Use this size" selects the
         matching variant immediately without a page reload.
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const STORAGE_PREFIX = 'tyk.size.';

  // ── Find the Size option input matching a given size label ──────────
  // The PDP uses data-tyk-picker blocks with option chips. We normalize
  // comparisons: case-insensitive, stripped of spaces, forgives "XL" vs
  // "X-Large", "2X3X" vs "2X-3X", etc.
  function normalize(s) {
    return String(s || '').toLowerCase().replace(/[\s\-_/]+/g, '');
  }

  function matchesSizeLabel(value, target) {
    const v = normalize(value);
    const t = normalize(target);
    if (!v || !t) return false;
    if (v === t) return true;
    // "XL" ≈ "extralarge"
    if (t === 'xl' && (v === 'xlarge' || v === 'extralarge')) return true;
    if (v === 'xl' && (t === 'xlarge' || t === 'extralarge')) return true;
    // "2X3X" ≈ "2x" or "3x" combined
    if (t === '2x3x' && (v === '2x' || v === '3x' || v === '2xl' || v === '3xl')) return true;
    // Substring fallback
    return v.indexOf(t) !== -1 || t.indexOf(v) !== -1;
  }

  function findSizePickerIn(root) {
    // Look for a tyk-picker whose head label says "Size"
    const pickers = root.querySelectorAll('[data-tyk-option-index]');
    for (let i = 0; i < pickers.length; i++) {
      const head = pickers[i].querySelector('.tyk-picker__head');
      if (head && /size/i.test(head.textContent || '')) return pickers[i];
    }
    // Fallback — any picker with radios whose value looks like a size
    return pickers[0] || null;
  }

  function selectSize(root, sizeId) {
    const picker = findSizePickerIn(root);
    if (!picker) return false;
    const radios = picker.querySelectorAll('input[data-tyk-option-input]');
    for (let i = 0; i < radios.length; i++) {
      if (matchesSizeLabel(radios[i].value, sizeId)) {
        if (!radios[i].checked) {
          radios[i].checked = true;
          // Fire change so the PDP's variant logic picks it up
          radios[i].dispatchEvent(new Event('change', { bubbles: true }));
          radios[i].dispatchEvent(new Event('input', { bubbles: true }));
        }
        // Visual state — theme uses .is-active on the label wrapper
        const label = radios[i].closest('.tyk-chip-radio');
        if (label) {
          picker.querySelectorAll('.tyk-chip-radio').forEach(l => l.classList.remove('is-active'));
          label.classList.add('is-active');
        }
        return true;
      }
    }
    return false;
  }

  // Highlight the matching row in the size chart
  function highlightChartRow(sizeId) {
    document.querySelectorAll('[data-tyk-size-chart] tbody tr').forEach(function (tr) {
      tr.classList.remove('is-your-size');
      if (tr.getAttribute('data-size') === sizeId) tr.classList.add('is-your-size');
    });
  }

  // ── Detect chart type for the current product ──────────────────────
  // Priority:
  //   1. <product-form data-tyk-chart-type="diapers">
  //   2. <body data-tyk-chart-type="diapers">
  //   3. The product type slug matches known types
  //   4. Product has a tag like "diapers" / "snappies" / "denim"
  //   5. Fallback: look for a visible size chart section on the page
  function detectChartType(root) {
    const KNOWN = ['diapers', 'snappies', 'denim'];
    const scopes = [root, document.body];
    for (let i = 0; i < scopes.length; i++) {
      const el = scopes[i];
      if (!el) continue;
      const explicit = el.getAttribute && el.getAttribute('data-tyk-chart-type');
      if (explicit && KNOWN.indexOf(explicit) !== -1) return explicit;
    }
    // Size chart section present?
    const chart = document.querySelector('[data-tyk-size-chart][data-chart-type]');
    if (chart) {
      const t = chart.getAttribute('data-chart-type');
      if (KNOWN.indexOf(t) !== -1) return t;
    }
    return null;
  }

  // ── Public API ──────────────────────────────────────────────────────
  function applyToNearestForm(trigger, sizeId) {
    const form = (trigger && trigger.closest && trigger.closest('product-info, product-form, .tyk-pdp__form, form')) || document;
    const root = form === document ? document : form;
    const applied = selectSize(root, sizeId);
    highlightChartRow(sizeId);
    if (applied && window.tykNative && window.tykNative.toast) {
      window.tykNative.toast('Size ' + sizeId + ' selected', 'success');
    } else if (!applied && window.tykNative && window.tykNative.toast) {
      // The size wasn't available as a variant — friendly notice.
      window.tykNative.toast('Size ' + sizeId + ' saved — not offered on this product', 'error');
    }
    return applied;
  }

  function restoreFromStorage() {
    // Find every PDP form on the page (quick view can add more)
    const forms = document.querySelectorAll('product-info, product-form, .tyk-pdp__form');
    if (!forms.length) return;

    forms.forEach(function (form) {
      const type = detectChartType(form);
      if (!type) return;
      let saved;
      try { saved = localStorage.getItem(STORAGE_PREFIX + type); } catch (e) { saved = null; }
      if (!saved) return;
      // Only auto-select if nothing has been picked yet that differs
      // from the first variant default.
      selectSize(form, saved);
      highlightChartRow(saved);
    });
  }

  // ── Size-chart link behavior ────────────────────────────────────────
  // "📏 Size chart" links open the size-chart popup (rendered in the PDP
  // section as [data-tyk-chart-modal]). The chart inside is a <details>;
  // make sure it's expanded so the modal shows the table, not a collapsed
  // summary. Falls back to the old in-page anchor scroll if no modal is
  // present (e.g. a page that embeds the chart inline).
  function openChartModal() {
    const modal = document.querySelector('[data-tyk-chart-modal]');
    if (!modal) return false;
    const details = modal.querySelector('details[data-tyk-size-chart]');
    if (details && !details.open) details.open = true;
    modal.hidden = false;
    // Next frame so the open transition can play from the hidden state.
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    document.body.classList.add('tyk-chart-modal-open');
    return true;
  }

  function closeChartModal() {
    const modal = document.querySelector('[data-tyk-chart-modal]');
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('tyk-chart-modal-open');
    // Hide after the transition so it's removed from the a11y tree.
    setTimeout(function () { modal.hidden = true; }, 220);
  }

  function wireChartLinks() {
    document.addEventListener('click', function (e) {
      // Open
      const link = e.target.closest && e.target.closest('[data-tyk-size-chart-link], a[href="#tyk-sizing"]');
      if (link) {
        if (openChartModal()) {
          e.preventDefault();
        } else {
          // No modal on this page — fall back to expanding an inline chart.
          const target = document.getElementById('tyk-sizing');
          if (target && target.tagName === 'DETAILS' && !target.open) target.open = true;
        }
        return;
      }
      // Close (scrim or close button)
      if (e.target.closest && e.target.closest('[data-tyk-chart-close]')) {
        e.preventDefault();
        closeChartModal();
      }
    });

    // Esc closes the popup.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeChartModal();
    });
  }

  // ── Kickoff ─────────────────────────────────────────────────────────
  function init() {
    restoreFromStorage();
    wireChartLinks();
    // Re-run when product-info swaps content (variant change, etc.)
    document.addEventListener('tyk:size:saved', function (e) {
      if (e && e.detail && e.detail.size) highlightChartRow(e.detail.size);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.tykSizeAutoSelect = {
    applyToNearestForm: applyToNearestForm,
    restoreFromStorage: restoreFromStorage,
    selectSize: selectSize,
    highlightChartRow: highlightChartRow,
  };
})();
